<template>

  <div class="text-content">
    <h2 class="text-2xl mb-6">Terms of Use</h2>
    <p>
      Welcome! Please read these Terms of Use (“<strong>Terms</strong>”) carefully
      because they form a legally binding agreement between you and Significan’t (UK)
      Limited and Sign Language Interactions Limited (“<strong>we</strong>”,
      “<strong>us</strong>” or “<strong>our</strong>”)
    </p>
    <p>
      These Terms apply to visitors of Significan’t UK’s, Sign Language Interactions
      Limited’s and their affiliates’ websites located at
      <a href="https://www.signvideo.co.uk" target="_blank">www.signvideo.co.uk</a>,
      <a href="https://www.sorenson.co.uk" target="_blank">www.sorenson.co.uk</a>,
      <a href="https://www.signlanguageinteractions.com/" target="_blank">www.signlanguageinteractions.com/</a>,
      their subdomains, all websites that link to these Terms (collectively, the
      “<strong>Sites</strong>”) and users of our information and technology services
      accessible through the Sites (the “<strong>Services</strong>”; together with the
      Sites, the “<strong>Site Services</strong>”) and governs the use of the Site
      Services.  By accessing or using the Site Services, you automatically agree to
      comply with these Terms.  If you do not agree to these Terms, you should not use
      this Site or the Services.
    </p>
    <p>
      <strong>1. Privacy Policy.</strong> Your use of the Site Services and the
      information we collect about you during your visit to the Sites is governed by
      our <a :href="privacyPolicyUrl" target="_blank">Privacy Policy</a>. The
      <a :href="privacyPolicyUrl" target="_blank">Privacy Policy</a> describes how we
      and our third-party partners collect, use, share and disclose information about
      you when you visit the Sites or use any Service. Your use of the Site Services
      does not, by itself, constitute consent to the processing of your personal data.
      Where we rely on consent as a lawful basis for processing, we will seek your
      consent separately and in accordance with applicable data protection law. You may
      withdraw your consent at any time by contacting us through the mechanisms
      described in our Privacy Policy.
    </p>
    <p>
      <strong>2. Access.</strong> Please note that in order to access and use certain
      Services within the Sites, you must register for an account.  To create an
      account, you must be at least 18 years of age, possess the legal right and
      ability to enter into a legally binding agreement with us and agree and warrant
      to use the Sites and Services therein in accordance with these Terms.  If you
      sign up for our Services, you may be asked to sign up to the terms of service or
      user agreement governing such Services (collectively, the “<strong>User
      Agreement</strong>”). The User Agreement will govern your use of the Services in
      addition to these Terms. If there is a conflict between these Terms and the User
      Agreements, the term of the User Agreement will control with respect to the
      Services.
    </p>
    <p>
      <strong>3. Modifications.</strong> At any time, we may modify these Terms by
      posting the modified language on the Site Services, with or without notice to
      you. Posted modifications will become effective immediately, and your use of the
      Site Services following any such modifications constitutes your agreement to be
      bound by the Terms as modified.  We reserve the right to modify, add, suspend or
      discontinue any aspect, service, Content or feature of the Sites at any time,
      with or without notice to you.
    </p>
    <p>
      <strong>4. Content.</strong> The Site Services includes text, data, graphics,
      materials, designs, images, videos or other content (collectively,
      “<strong>Content</strong>”) created by us or third parties.  The Content is
      provided for general information purposes only and should not be relied upon for
      personal, professional, medical, legal, regulatory or other decisions.  We do not
      ensure completeness, timeliness or accuracy of the Content.
    </p>
    <p>
      <strong>5. License and Restrictions.</strong> Subject to the restrictions in
      these Terms, we grant you a limited, personal, nonexclusive, revocable license to
      access and use the Site Services and its Content solely for your personal,
      noncommercial and internal use, and only in a manner that complies with all legal
      requirements that apply to you or your use of the Site Services and its Content,
      including these Terms, and provided you do not modify the Site Services, its
      Content or any copyright or proprietary notices.  You may not modify, copy,
      distribute, mirror, frame, publicly display, publicly perform, reproduce,
      translate, publish, license, create derivative works of, or transmit the Site
      Services or Content (in whole or in part) in any way through any medium for
      distribution, publication or any commercial purpose.  You will not use the Site
      Services in any manner that could damage, disable or impair the Site Services or
      interfere with the Site Services or any feature of the Site Services or other’s
      use of the Site Services, including any use that violates any laws, infringes on
      anyone’s rights or is offensive.  Your use of the Site Services is at our
      discretion and we can terminate your use, prohibit you from using the Site
      Services or take appropriate legal actions at any time.
    </p>
    <p>
      <strong>6. Ownership.</strong> We and/or our licensors own all rights, title and
      interest in and to the Site Services, and the Content, including, without
      limitation, the trademarks, logos and icons and the structure, organization and
      arrangement thereof, which are protected by law, including, but not limited to,
      copyright, trademark, trade dress and various other intellectual property and
      unfair competition laws.
    </p>
    <p>
      <strong>7. Links to Other Sites.</strong> The Site Services may contain
      hyperlinks to websites operated by third parties or to materials or information
      made available by third parties.  The placement of such links on the Site
      Services does not constitute or imply our endorsement of such third parties, the
      content of their websites, their products or services, or the privacy or security
      practices of those websites.  We are not responsible or liable for (i) any
      content, products, advertising, information or materials on or available from
      such websites or resources, (ii) any errors or omissions in those websites or
      resources, or (iii) any information handling or other business practices of the
      operators of such websites and resources.  You acknowledge and agree that we will
      not be responsible or liable, directly or indirectly, for any damages or losses
      caused or alleged to be caused by or in connection with use of or reliance on any
      linked sites or resources.  Your interactions with such third parties will be
      governed by the third parties’ own terms of services and privacy policies, and
      any other terms.
    </p>
    <p>
      <strong>8. No Legal or Regulatory Advice.</strong> Some Content may include
      legal or regulatory related information pertaining to you or your business.  Such
      Content is provided for general informational purposes only.  We are not
      providing legal or regulatory advice and no professional advisory relationship is
      created by your use of the Site Services or the Content. You should seek the
      advice of your legal representatives or advisors with any questions you may have
      regarding a law, regulation, or dispute.
    </p>
    <p>
      <strong>9. Compliance with Law.</strong> As a condition of your use of the Site
      Services, you warrant to us that you will not use the Site Services for any
      purpose that is unlawful, offensive, interferes with the Site Services or any
      feature therein, infringes anyone’s rights or prohibited by these Terms. You
      agree to comply with all applicable national, country and local laws regarding
      your use of the Site Services and the Content. You acknowledge that your
      unauthorized use of the Site Services or any Content may violate copyright laws
      and trademark laws, as well as other regulations and statutes. If you reside
      outside the United Kingdom you also agree to comply with any local laws
      applicable to your use of the Site Services and Content.
    </p>
    <p>
      <strong>10. Submissions and Communication.</strong> You agree that any
      information you submit through the Site Services is accurate and does not include
      any confidential or sensitive information. By submitting information to us
      through the Site Services, you agree that we may use that information in any way,
      for any purpose, including our own commercial purpose, and that such submitted
      information becomes our property without any obligation and you are not entitled
      to any accounting, compensation or reimbursement of any kind from us under any
      circumstances.  When you use the Site Services or send communications to us
      through the Site Services, you are communicating with us electronically. You
      consent to receive electronically any communications related to your use of the
      Site Services. You agree that all agreements, notices, disclosures and other
      communications that are provided to you electronically satisfy any legal
      requirement that such communications be in writing. All notices from us intended
      for receipt by you will be deemed delivered and effective when sent to the email
      address you provide to us. By submitting information or otherwise providing us
      with your name, email address, postal address or phone number, you are agreeing
      that we or our agents may contact you at that address or number in a manner
      consistent with the <a :href="privacyPolicyUrl" target="_blank">Privacy Policy</a>.
    </p>
    <p>
      <strong>11. Feedback on the Site.</strong> The Site Services may have certain
      features that allow you to submit comments, information, and other materials
      (collectively, “<strong>Feedback</strong>”) to us and share such Feedback with
      other third parties or the public. By submitting Feedback through the Site
      Services or otherwise providing us with any Feedback through any means, you grant
      us a perpetual, irrevocable, transferable and sublicensable right and license to
      access, use, copy, reproduce, process, adapt, publish, transmit, host, and
      display that Feedback for any purpose (including in testimonials or our other
      marketing materials and where required to do so by law or in good faith to comply
      with legal process). We reserve the right to remove any Feedback posted in public
      forums on the Site Services, if any, for any reason at our sole discretion.
    </p>
    <p>
      <strong>12. Indemnification.</strong> You will indemnify, defend and hold
      harmless us and other visitors or users of the Site Services, and our and their
      affiliates, officers, directors, employees, agents, representatives, partners and
      licensors from and against any and all claims, actions, proceedings, suits,
      liabilities, damages, settlements, penalties, fines, losses, costs, disbursements
      or expenses, including attorneys’ fees, arising out of or in any way connected
      with your access to or use of the Site Services, the Content, your violation of
      these Terms, your violation of applicable laws and regulations, your violation of
      any rights of another individual or entity, your negligent or willful misconduct,
      or any emotional or physical harm to you or any third party resulting from your
      access to or use of the Site Services or Content
    </p>
    <p>
      <strong>13. Disclaimer.</strong> The Site Services and the Content contained in
      the Site Services may include inaccuracies or typographical or other errors. WE,
      OUR AFFILIATES AND OUR RESPECTIVE SUPPLIERS, LICENSORS, EMPLOYEES, AGENTS AND
      REPRESENTATIVES MAKE NO REPRESENTATIONS ABOUT THE SUITABILITY, RELIABILITY,
      TIMELINESS AND ACCURACY OF THE INFORMATION, CONTENT, SERVICES AND RELATED TEXTS
      AND GRAPHICS CONTAINED ON THE SITE SERVICES.  ALL SUCH INFORMATION, THE SITE
      SERVICES AND THE CONTENT ON THE SITES ARE PROVIDED ON AN “AS IS” AND “AS
      AVAILABLE” BASIS WITHOUT WARRANTY OF ANY KIND. YOU AGREE THAT USE OF THE SITES IS
      AT YOUR SOLE RISK. WE, OUR AFFILIATES AND OUR RESPECTIVE SUPPLIERS, LICENSORS,
      EMPLOYEES, AGENTS AND REPRESENTATIVES HEREBY DISCLAIM ALL WARRANTIES OF ANY KIND,
      WHETHER EXPRESS, IMPLIED, STATUTORY OR OTHERWISE, INCLUDING BUT NOT LIMITED TO,
      THE IMPLIED WARRANTIES OF MERCHANTABILITY, SATISFACTORY QUALITY, FITNESS FOR A
      PARTICULAR PURPOSE, ACCURACY, TITLE AND NON-INFRINGEMENT.  WITHOUT LIMITING THE
      FOREGOING, WE DO NOT WARRANT THAT THE SITE SERVICES WILL MEET YOUR REQUIREMENTS,
      WILL BE UNINTERRUPTED, ERROR FREE, RELIABLE, WITHOUT DELAY, CURRENT, COMPLETE,
      WITHOUT BREACHES OF SECURITY OR THAT THE DEFECTS IN THE SITE SERVICES WILL BE
      CORRECTED. YOU EXPRESSLY AGREE THAT YOUR USE OF THE SERVICES IS AT YOUR SOLE
      RISK.  YOU ARE SOLELY RESPONSIBLE FOR ANY AND ALL ACTS OR OMISSIONS TAKEN OR MADE
      IN RELIANCE ON THE SITE SERVICES OR CONTENT, INCLUDING INACCURATE OR INCOMPLETE
      INFORMATION.  IF YOU ARE DISSATISFIED WITH THE SITE SERVICES OR ANY CONTENT, YOUR
      SOLE AND EXCLUSIVE REMEDY IS TO DISCONTINUE USING AND ACCESSING THE SITE
      SERVICES.  SOME JURISDICTIONS DO NOT ALLOW THE EXCLUSION OR LIMITATION OF IMPLIED
      WARRANTIES OR LIMITATIONS ON APPLICABLE STATUTORY RIGHTS OF A CONSUMER, SO IN
      THESE JURISDICTIONS THE ABOVE LIMITATIONS OR EXCLUSIONS MAY NOT APPLY TO YOU.
    </p>
    <p>
      <strong>14. Limitation of Liability.</strong> TO THE EXTENT NOT PROHIBITED BY
      LAW, UNDER NO CIRCUMSTANCES WILL WE, OUR AFFILIATES, OR OUR RESPECTIVE OFFICERS,
      DIRECTORS, EMPLOYEES, AGENTS, REPRESENTATIVES, PARTNERS AND LICENSORS BE LIABLE
      OR RESPONSIBLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, CONSEQUENTIAL (INCLUDING
      DAMAGES FROM LOSS OF BUSINESS, LOST PROFITS, LOSS OF GOODWILL, LOSS OF
      INFORMATION, LITIGATION, OR THE LIKE), SPECIAL, EXEMPLARY, PUNITIVE, OR OTHER
      DAMAGES, WHETHER BASED ON CONTRACT, WARRANTY, NEGLIGENCE, STRICT LIABILITY OR
      UNDER ANY OTHER THEORY OF LIABILITY, ARISING OUT OF OR IN ANY WAY RELATING TO THE
      SITES OR SERVICES, YOUR USE OF THE SITES OR SERVICES, OR THE CONTENT, EVEN IF
      ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. YOUR SOLE AND EXCLUSIVE REMEDY FOR
      DISSATISFACTION WITH THE SITES, CONTENT AND/OR THESE TERMS IS TO DISCONTINUE YOUR
      USE OF THE SITES.  IN THE CASE OF A JURISDICTION THAT RESTRICTS LIMITATION
      CLAUSES, THIS LIMITATION WILL BE APPLIED TO THE GREATEST EXTENT PERMITTED BY LAW.
      NOTHING IN THESE TERMS IS INTENDED TO LIMIT ANY RIGHTS YOU MAY HAVE THAT MAY NOT
      BE LAWFULLY TERMINATED.
    </p>
    <p>
      <strong>15. Governing Law; Jurisdictions; Disputes.</strong> The interpretation
      of these Terms and the resolution of any disputes arising under these Terms shall
      be governed by the laws of England and Wales, without regard to its conflict of
      laws provisions.  These Terms shall not be governed by the United Nations
      Convention on Contract for the International Sale of Goods, the application of
      which is expressly disclaimed.  If any action or other proceeding is brought on
      or in connection with these Terms, you agree to submit to the personal
      jurisdiction of the courts located in the London, England, and agree not to bring
      any action relating to the use of the Sites or these Terms in any court in any
      other jurisdiction. We will have the right to commence and prosecute any legal or
      equitable action or proceeding before any UK or United States court of competent
      jurisdiction to enforce these Terms or to protect our or any third party’s rights
      in the Sites Services or the Content.  You also agree that we may bring suit in
      court to enjoin any violation of these Terms without the posting of a bond or
      security, in addition to whatever remedies we might have at law.  In any dispute
      between you and us where we prevail, we will be entitled to recover legal costs.
    </p>
    <p>
      <strong>16. Waiver and Severability.</strong> Failure to enforce any provision in
      these Terms will not constitute a waiver of that provision. If any provision is
      held by a court of competent jurisdiction to be contrary to law, the provision
      will be modified by the court and interpreted so as best to accomplish the
      objectives of the original provision to the fullest extent permitted by law, and
      the remaining provisions of these Terms will remain in effect.
    </p>
    <p>
      <strong>17. Entire Agreement.</strong> These Terms, together with any terms and
      policies that are incorporated by reference, constitute the entire agreement and
      supersede any prior agreements between you and us with respect to your use of the
      Sites and Content.  We may freely assign these Terms in connection with a merger,
      acquisition, or sale of assets, or by operation of law or otherwise.
    </p>
    <p>
      <strong>18. Reservation of Rights.</strong> SignVideo, Sorenson Forum, and
      Sorenson Communications are trademark or registered trademarks of Significan’t
      (UK) Limited and/or its affiliates. Other products and company names mentioned
      herein may be the trademarks of their respective owners.
    </p>
    <p>
      Last Updated: July 29, 2026
    </p>
    <p>
      Except for the limited right granted herein, all other rights are reserved.
      Except as otherwise required or limited by applicable law, any reproduction,
      distribution, modification, retransmission or publication of any copyrighted
      materials is strictly prohibited without the express written consent of the
      copyright owner.
    </p>
  </div>
</template>

<style scoped lang="scss">
    .text-content {
      font-size: 15px;

        a {
            color: #6CBFFF;
        }
    }

    p {
      margin: 15px 0;
    }

</style>
<script setup lang="ts">
import { Constants } from '@/cfg/constants';

const privacyPolicyUrl = Constants.REGION_CONFIG.privacyUrl;
</script>
